import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "components/Modal/Modal";
import { PrimaryButton } from "components/Button/PrimaryButton";

interface ReanalyzeModalProps {
  show: boolean;
  // Prefills the textarea with the session's current additional context so
  // the user can refine instead of retype.
  defaultContext: string;
  onClose: () => void;
  onConfirm: (additionalContext: string) => void;
}

// "Analyze again" confirmation: lets the user add/refine context and spells
// out the merge semantics BEFORE anything happens — only `suggested` hazards
// are replaced; generated/accepted/dismissed hazards are untouched and groups
// are merged by label (plan §1 / contract endpoint 2).
export const ReanalyzeModal = ({
  show,
  defaultContext,
  onClose,
  onConfirm,
}: ReanalyzeModalProps) => {
  const { t } = useTranslation();
  const [context, setContext] = useState(defaultContext);

  useEffect(() => {
    if (show) {
      setContext(defaultContext);
    }
  }, [show, defaultContext]);

  return (
    <Modal
      show={show}
      onClose={onClose}
      width="1/2"
      content={
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {t("risks.ai.reanalyze.headline")}
          </h3>
          <p className="mt-2 text-sm text-gray-500 whitespace-pre-line">
            {t("risks.ai.reanalyze.subline")}
          </p>

          <label
            htmlFor="ai-risk-reanalyze-context"
            className="mt-4 block text-sm font-medium text-gray-700"
          >
            {t("risks.ai.reanalyze.context.label")}
          </label>
          <textarea
            id="ai-risk-reanalyze-context"
            rows={4}
            value={context}
            onChange={(event) => setContext(event.target.value)}
            placeholder={t("risks.ai.reanalyze.context.placeholder")}
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
          />

          <div className="mt-6 flex justify-end items-center gap-4">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {t("steps.risks.confirmation.cancel")}
            </button>
            <PrimaryButton
              testId="ai-risk-reanalyze"
              onClick={() => onConfirm(context)}
            >
              {t("risks.ai.reanalyze.cta")}
            </PrimaryButton>
          </div>
        </div>
      }
    />
  );
};
